/**
 * `isFreshSite` — ported faithfully from
 * `docs/reference/Foyer_Dashboard_CURRENT.jsx` line 157.
 *
 * A site is "fresh" (empty-state candidate, sample-traffic eligible) when it
 * has zero observed calls AND either it's still verifying OR it was added
 * within the last 48 hours. Used by SampleTrafficCtx to decide whether the
 * sample-traffic toggle should surface at all.
 *
 * In the reference the JS `Site` shape carries `stats.calls` and `addedAt`;
 * in our typed model these are not on `Site` (they're per-site aggregates).
 * The function therefore takes a `FreshSiteInput` projection so callers can
 * supply the relevant fields from either the typed `Site` plus a separate
 * `calls` count, or from a richer mock site shape.
 */

export interface FreshSiteInput {
  /** Total calls observed; if undefined the site is treated as zero-calls. */
  calls?: number;
  /** Site lifecycle status. */
  status?: string;
  /** When the site was added (ISO-8601). */
  added_at?: string;
}

const FORTY_EIGHT_HOURS_MS = 48 * 60 * 60 * 1000;

export function isFreshSite(site: FreshSiteInput | null | undefined): boolean {
  if (!site) {
    return false;
  }
  // Any real traffic forces non-fresh, regardless of status or age.
  if (typeof site.calls === 'number' && site.calls > 0) {
    return false;
  }
  if (site.status === 'verifying' || site.status === 'pending_verification') {
    return true;
  }
  if (!site.added_at) {
    return false;
  }
  const added = new Date(site.added_at);
  const ms = added.getTime();
  if (Number.isNaN(ms)) {
    return false;
  }
  const hoursSince = (Date.now() - ms) / FORTY_EIGHT_HOURS_MS;
  return hoursSince < 1;
}
